import { DATE_FILTERS } from '@constants/filters';
import useLocale from '@hooks/useLocale';
import { useOverview } from '@hooks/useOverview';
import { getEventPages } from '@lib/prepr/event';
import Filters from '@molecules/Filters/Filters';
import CardGrid from '@organisms/CardGrid/CardGrid';
import Hero from '@organisms/Hero/Hero';
import Sections from '@organisms/Sections/Sections';
import { FilterGroup } from '@type-declarations/filters';
import { EventOverviewPage } from '@type-declarations/page';
import { groupCategoryFilters } from '@utils/filters/categoryFilter';
import { tagToFilter } from '@utils/filters/tagFilter';
import { useRef } from 'react';

interface Props {
  page: EventOverviewPage;
}

const translations = {
  nl: {
    searchLabel: 'Zoek naar evenement',
    tags: 'Kenmerken',
    categories: 'Categorieën',
    nothingFoundTitle: 'Er zijn geen evenementen gevonden',
    nothingFoundText: 'Pas uw filter input aan of probeer het later nog eens.',
    today: 'Vandaag',
    tomorrow: 'Morgen',
    weekend: 'Weekend',
  },
  en: {
    searchLabel: 'Search for event',
    tags: 'Tags',
    categories: 'Categories',
    nothingFoundTitle: 'No events found',
    nothingFoundText: 'Try changing your filter input or try again later.',
    today: 'Today',
    tomorrow: 'Tomorrow',
    weekend: 'Weekend',
  },
};

function EventOverviewTemplate({ page }: Props) {
  const locale = useLocale();

  const scrollToRef = useRef<HTMLDivElement | null>(null);

  const {
    title,
    introText,
    image,
    items: initItems,
    total: initTotal,
    alignment,
    highlightedTag,
  } = page;
  const {
    total,
    items,
    activeFilters,
    clearFilters,
    handleChange,
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    handleNextPage,
    handlePrevPage,
    handleGoToPage,
    loading,
  } = useOverview({
    initItems,
    initTotal,
    scrollToRef,
    fetch: getEventPages,
  });

  const categoryGroups: FilterGroup[] = groupCategoryFilters(page.categories);

  return (
    <>
      <Hero
        title={title}
        text={introText}
        image={image}
        colorVariant="gray"
        alignment={alignment}
      />

      <Filters
        highlightedTag={highlightedTag}
        primary={[
          {
            key: 'date',
            filters: [
              {
                value: DATE_FILTERS.TODAY,
                title: translations[locale].today,
              },
              {
                value: DATE_FILTERS.TOMORROW,
                title: translations[locale].tomorrow,
              },
              {
                value: DATE_FILTERS.WEEKEND,
                title: translations[locale].weekend,
              },
            ],
          },
        ]}
        secondary={[
          ...categoryGroups,
          {
            title: translations[locale].tags,
            key: 'tags',
            filters: tagToFilter(page.tags),
          },
        ]}
        activeFilters={activeFilters}
        searchLabel={translations[locale].searchLabel}
        hasDateFilter
        onChange={handleChange}
        onClear={clearFilters}
      />

      <CardGrid
        total={total}
        loading={loading}
        notFoundTitle={translations[locale].nothingFoundTitle}
        notFoundText={translations[locale].nothingFoundText}
        items={items}
        scrollToRef={scrollToRef}
        currentPage={currentPage}
        totalPages={totalPages}
        hasPrevPage={hasPrevPage}
        hasNextPage={hasNextPage}
        handlePrevPage={handlePrevPage}
        handleNextPage={handleNextPage}
        handleGoToPage={handleGoToPage}
      />

      <Sections sections={page.sections} />
    </>
  );
}

export default EventOverviewTemplate;
